/** @jsxImportSource @emotion/react */
import { CardContainer } from "../../../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../../../components/page-skeleton/PageHeader";
import { AdyenAccountHolder } from "../../models/adyen-account-holder";
import { routes } from "../../../../appRoutingHelpers";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../auth/authSelectors";
import {
  ButtonExternalLink,
  ButtonInternalLink,
  ButtonPrimary,
} from "../../../../components/NewButtons";
import { colors } from "../../../../components/colors";
import { Divider } from "../../../../components/Divider";
import { CardInformationRow } from "../../../../components/page-skeleton/CardInformationRow";
import { StyledText } from "../../../../assets/typography/typography";
import {
  useGetAdyenAccountHolderOnboardingLink,
  useGetAdyenAccountHolderVerificationErrors,
  usePushAdyenAccountHolderToAdyen,
} from "../../service/adyen-account-holders-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { css } from "@emotion/react";
import { SpinnerBlock } from "../../../common/Spinner";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../../error-handler";
import { ActionSpinner } from "../../../../components/ActionSpinner";

const listStyle = css`
  margin: 0;
  padding-left: 16px;
`;

type Props = {
  accountHolder: AdyenAccountHolder;
};

export const AdyenAccountHolderInspect = ({ accountHolder }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    data: verificationErrors,
    isLoading: isVerificationErrorsLoading,
  } = useGetAdyenAccountHolderVerificationErrors(
    accountHolder.id,
    !!accountHolder.reference
  );

  const {
    data: onboardingLink,
    isLoading: isOnboardingLinkLoading,
  } = useGetAdyenAccountHolderOnboardingLink(
    accountHolder.id,
    !!accountHolder.reference
  );

  const {
    mutateAsync: pushAdyenAccountHolderToAdyen,
    isLoading: isPushingAdyenAccountHolderToAdyen,
  } = usePushAdyenAccountHolderToAdyen();

  const handlePushAdyenAccountHolderToAdyen = async () => {
    try {
      await pushAdyenAccountHolderToAdyen({ data: { id: accountHolder.id } });
      toastr.success("Success", "Adyen account holder pushed to Adyen");
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  if (isOnboardingLinkLoading || isVerificationErrorsLoading) {
    return <SpinnerBlock />;
  }

  return (
    <CardContainer>
      <PageHeader title="Account holder inspect">
        <ButtonInternalLink
          to={routes.adyenAccountHolderEdit(
            organizationKeyName,
            accountHolder.id
          )}
        >
          Edit account holder
        </ButtonInternalLink>
        <ButtonPrimary
          disabled={isPushingAdyenAccountHolderToAdyen}
          icon={<FontAwesomeIcon icon={solid("upload")} />}
          onClick={handlePushAdyenAccountHolderToAdyen}
        >
          {isPushingAdyenAccountHolderToAdyen ? (
            <ActionSpinner />
          ) : (
            "Push to Adyen"
          )}
        </ButtonPrimary>
      </PageHeader>
      <div>
        <Divider />
        <CardInformationRow title="Organization">
          <StyledText
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
          >
            {accountHolder.organizationName ?? "-"}
          </StyledText>
        </CardInformationRow>
        <CardInformationRow title="Country">
          <StyledText
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
          >
            {accountHolder.countryCode || "-"}
          </StyledText>
        </CardInformationRow>
        <CardInformationRow title="Description">
          <StyledText
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
          >
            {accountHolder.description || "-"}
          </StyledText>
        </CardInformationRow>
        <CardInformationRow title="Onboarding link">
          {onboardingLink ? (
            <ButtonExternalLink
              href={onboardingLink.onboardingLink}
              icon={<FontAwesomeIcon icon={solid("up-right-from-square")} />}
            >
              Go to onboarding
            </ButtonExternalLink>
          ) : (
            <StyledText
              className="bodyTextSemibold mb-0"
              color={colors.textPrimary}
            >
              -
            </StyledText>
          )}
        </CardInformationRow>
        <CardInformationRow title="Verification errors">
          {verificationErrors?.length && verificationErrors.length > 0 ? (
            <ul css={listStyle}>
              {verificationErrors?.map((error, index) => (
                <li key={index}>
                  <StyledText
                    className="bodyTextSemibold mb-0"
                    color={colors.errorDark}
                  >
                    {error.message} ({error.code})
                  </StyledText>
                </li>
              ))}
            </ul>
          ) : (
            <StyledText
              className="bodyTextSemibold mb-0"
              color={colors.textPrimary}
            >
              -
            </StyledText>
          )}
        </CardInformationRow>
        <CardInformationRow title="Adyen account holder reference">
          <StyledText
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
          >
            {accountHolder.reference || "-"}
          </StyledText>
        </CardInformationRow>
        <CardInformationRow title="Adyen legal entity reference">
          <StyledText
            className="bodyTextSemibold mb-0"
            color={colors.textPrimary}
          >
            {accountHolder.legalEntityReference || "-"}
          </StyledText>
        </CardInformationRow>
        {accountHolder.reference && (
          <CardInformationRow title="Capabilities">
            <ButtonInternalLink
              to={routes.adyenAccountHolderCapabilities(
                organizationKeyName,
                accountHolder.id
              )}
              icon={<FontAwesomeIcon icon={solid("up-right-from-square")} />}
            >
              See capabilities
            </ButtonInternalLink>
          </CardInformationRow>
        )}
      </div>
    </CardContainer>
  );
};
